
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  ClearRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { Alert, Dialog, Table } from "../../layout";

import ViewProd from "./ViewProd";
import ReportPrintButton from "../reports/ReportPrintButton";

import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";

import { AppContext } from "../../App";
import { DropLi } from "../../hooks";
import { IsEmptyObj } from "../../hooks/Method";
import { formatCurrency } from "../../hooks/Currency";

//------------------------------------------------
// Sales Transactions
//------------------------------------------------
const Sells = (props) => {
  const { t } = useTranslation();

  const {
    _list,
    _clear,
    _getById,
    _delete,
    _type,
    _store,
  } = props;

  const { setLoader } = useContext(AppContext);
  const { PayType } = DropLi();

  const reportRef = useRef(null);

  const [viewData, setViewData] = useState({});
  const [showForm, setShowForm] = useState(false);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Sales by Store
  //------------------------------------------------
  useEffect(() => {
    if (_store?.id) {
      setLoader(true);

      _getById(
        endpoints.Sell + controller.GetSellsByStore,
        _store.id,
        actions.ActionType.List,
        () => setLoader(false)
      );
    }

    return () => {
      _clear();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  //------------------------------------------------
  // Sales Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    if (
      _type !== "sells" ||
      !Array.isArray(_list)
    ) {
      return [];
    }

    return _list.map((obj) => {
      const paymentTypeName =
        (PayType || []).find(
          (item) =>
            String(item.id) ===
            String(obj.paymentType)
        )?.name || "";

      const cashierName = [
        obj.user?.firstName,
        obj.user?.lastName,
      ]
        .filter(Boolean)
        .join(" ");

      return {
        ...obj,

        totalItem: Array.isArray(obj.sellItems)
          ? obj.sellItems.length
          : 0,

        paymentType: paymentTypeName,

        received: formatCurrency(
          Number(obj.received) || 0,
          _store
        ),

        change: formatCurrency(
          Number(obj.change) || 0,
          _store
        ),

        username: cashierName,

        customername:
          obj.customer?.customerName ||
          t("walkInCustomer"),
      };
    });
  }, [_list, _type, _store, PayType, t]);

  //------------------------------------------------
  // View Sale Details
  //------------------------------------------------
  const handleView = (data) => {
    // Pass the original sale object to ViewProd.
    const original = Array.isArray(_list)
      ? _list.find(
          (item) =>
            String(item.id) === String(data.id)
        )
      : null;

    setViewData(original || data);
    setShowForm(true);
  };

  //------------------------------------------------
  // Delete Sale
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteSellConfirmation"),

      onConfirm: () => {
        setLoader(true);

        const onComplete = (res) => {
          setLoader(false);

          if (res.status === 200) {
            setAlert({
              isOpen: true,
              type: "success",
              title: t("successful"),
              subTitle: res?.msg,
            });
          } else {
            setAlert({
              isOpen: true,
              type: "error",
              title: t("error"),
              subTitle:
                res?.msg || t("deleteFailed"),
            });
          }
        };

        _delete(
          endpoints.Sell,
          id,
          actions.ActionType.RmvList,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    {
      id: "sellDate",
      label: t("transactionDate"),
    },
    { id: "id", label: t("invoice") },
    { id: "totalItem", label: t("totalItem") },
    {
      id: "paymentType",
      label: t("paymentType"),
    },
    {
      id: "received",
      label: t("paymentReceived"),
    },
    { id: "change", label: t("change") },
    { id: "username", label: t("cashier") },
    { id: "customername", label: t("customer") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("delete")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("salesTransactions")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("salesTransactions")}
        />
      </Box>

      {/* Normal Sales Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={() => <></>}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Sales Transactions */}
      <Box
        ref={reportRef}
        className="sales-transactions-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("salesTransactions")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />

      {/* Existing Sale Details Dialog */}
      <Dialog
        show={showForm}
        maxWidth="lg"
        onClose={false}
      >
        {!IsEmptyObj(viewData) && (
          <ViewProd
            setShowForm={setShowForm}
            viewData={viewData}
          />
        )}
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => ({
  _list: state.pos.list,
  _type: state.pos.type,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(Sells);
